import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import MainLayout from '@/layouts/MainLayout.vue';
import { navSections } from '@/layouts/nav';

const routes: RouteRecordRaw[] = [
  // ----------------------------------------------------------------------
  // Login (no layout, full screen)
  // ----------------------------------------------------------------------
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/Login.vue'),
    meta: { guest: true },
  },
  {
    path: '/org/:organizationSlug/login',
    name: 'organization-login',
    component: () => import('@/pages/Login.vue'),
    meta: { guest: true },
  },
  {
    path: '/internal-login',
    name: 'internal-login',
    component: () => import('@/pages/InternalLogin.vue'),
    meta: { guest: true },
  },
  {
    path: '/internal/login',
    redirect: { name: 'internal-login' },
  },

  // Accept-invite page — no auth required, the invite token IS the auth.
  // Marked public so authenticated users hitting this link can still accept
  // (e.g. forwarded the link to themselves).
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: () => import('@/pages/AcceptInvite.vue'),
    meta: { public: true },
  },

  // ----------------------------------------------------------------------
  // Public marketing pages (use PublicLayout)
  // ----------------------------------------------------------------------
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'landing',
        component: () => import('@/pages/Landing.vue'),
        meta: { public: true },
      },
      {
        path: 'help',
        name: 'help',
        component: () => import('@/pages/Help.vue'),
        meta: { public: true },
      },
      {
        path: 'about',
        name: 'about',
        component: () => import('@/pages/About.vue'),
        meta: { public: true },
      },
      {
        path: 'privacy',
        name: 'privacy',
        alias: ['/privacy-policy', '/legal/privacy'],
        component: () => import('@/pages/legal/Privacy.vue'),
        meta: { public: true },
      },
      {
        path: 'terms',
        name: 'terms',
        alias: ['/terms-of-service', '/legal/terms'],
        component: () => import('@/pages/legal/Terms.vue'),
        meta: { public: true },
      },
      {
        path: 'data-use',
        name: 'data-use',
        alias: ['/data-use-policy', '/legal/data-use'],
        component: () => import('@/pages/legal/DataUse.vue'),
        meta: { public: true },
      },
      {
        path: 'data-deletion',
        name: 'data-deletion',
        alias: ['/delete-data', '/data-deletion-instructions', '/legal/data-deletion'],
        component: () => import('@/pages/legal/DataDeletion.vue'),
        meta: { public: true },
      },
      {
        path: 'sub-processors',
        name: 'sub-processors',
        alias: ['/subprocessors', '/legal/sub-processors'],
        component: () => import('@/pages/legal/SubProcessors.vue'),
        meta: { public: true },
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Authenticated console pages (MainLayout)
  // ----------------------------------------------------------------------
  {
    path: '/app',
    component: MainLayout,
    children: [
      { path: '', name: 'dashboard', component: () => import('@/pages/Dashboard.vue') },
      { path: 'platforms', name: 'platforms', component: () => import('@/pages/Platforms.vue') },
      { path: 'reports', name: 'reports', component: () => import('@/pages/Reports.vue') },
      {
        path: 'pivot-report',
        name: 'pivot-report',
        component: () => import('@/pages/PivotReport.vue'),
      },
      {
        path: 'optimizer-efficiency',
        name: 'optimizer-efficiency-report',
        component: () => import('@/pages/OptimizerEfficiencyReport.vue'),
      },
      {
        path: 'diagnostics',
        name: 'diagnostics',
        component: () => import('@/pages/Diagnostics.vue'),
      },
      {
        path: 'creative-lifecycle',
        name: 'creative-lifecycle',
        component: () => import('@/pages/CreativeLifecycle.vue'),
      },
      {
        path: 'audience-lifecycle',
        name: 'audience-lifecycle',
        component: () => import('@/pages/AudienceLifecycle.vue'),
      },
      {
        path: 'data-quality',
        name: 'data-quality',
        component: () => import('@/pages/DataQuality.vue'),
      },
      {
        path: 'automation-rules',
        name: 'automation-rules',
        component: () => import('@/pages/AutomationRules.vue'),
      },
      {
        path: 'ai-assistant',
        name: 'ai-assistant',
        component: () => import('@/pages/AiAssistant.vue'),
      },
      { path: 'commerce', name: 'commerce', component: () => import('@/pages/Commerce.vue') },
      {
        path: 'profit',
        name: 'profit',
        component: () => import('@/pages/ProfitDashboard.vue'),
      },
      {
        path: 'profit-drilldown',
        name: 'profit-drilldown',
        component: () => import('@/pages/ProfitDrilldown.vue'),
      },
      {
        path: 'report-center',
        name: 'report-center',
        component: () => import('@/pages/ReportCenter.vue'),
      },
      {
        path: 'custom-report',
        name: 'custom-report',
        component: () => import('@/pages/CustomReport.vue'),
      },
      {
        path: 'profit-breakdown',
        name: 'profit-breakdown',
        component: () => import('@/pages/ProfitBreakdown.vue'),
      },
      {
        path: 'store-report',
        name: 'store-report',
        component: () => import('@/pages/StoreReport.vue'),
      },
      {
        path: 'test-planner',
        name: 'test-planner',
        component: () => import('@/pages/TestPlanner.vue'),
      },
      {
        path: 'experiments',
        name: 'experiments',
        component: () => import('@/pages/Experiments.vue'),
      },
      {
        path: 'insights',
        name: 'insights-library',
        component: () => import('@/pages/InsightsLibrary.vue'),
      },
      {
        path: 'ad-library',
        name: 'ad-library',
        component: () => import('@/pages/AdLibrary.vue'),
      },
      {
        path: 'autopilot',
        name: 'autopilot',
        component: () => import('@/pages/AutopilotDashboard.vue'),
      },
      {
        path: 'autopilot/policies',
        name: 'autopilot-policies',
        component: () => import('@/pages/AutopilotPolicies.vue'),
      },
      {
        path: 'autopilot/actions',
        name: 'autopilot-actions',
        component: () => import('@/pages/AutopilotActions.vue'),
      },
      {
        path: 'ad-management',
        name: 'ad-management',
        component: () => import('@/pages/AdManagement.vue'),
      },
      {
        path: 'batch-create',
        name: 'batch-create',
        component: () => import('@/pages/BatchCreateV2.vue'),
      },
      {
        path: 'one-click',
        name: 'one-click-launch',
        component: () => import('@/pages/OneClickLaunch.vue'),
      },
      // Legacy /campaigns route now redirects to the unified ad management page.
      // Detail route stays — Dashboard charts + automation rules still link to it.
      { path: 'campaigns', name: 'campaigns', redirect: { name: 'ad-management' } },
      {
        path: 'campaigns/:id',
        name: 'campaign-detail',
        component: () => import('@/pages/CampaignDetail.vue'),
      },
      {
        path: 'ad-accounts',
        name: 'ad-accounts',
        component: () => import('@/pages/AdAccounts.vue'),
      },
      { path: 'pages', name: 'pages', component: () => import('@/pages/Pages.vue') },
      { path: 'pixels', name: 'pixels', component: () => import('@/pages/Pixels.vue') },
      { path: 'creatives', name: 'creatives', component: () => import('@/pages/Creatives.vue') },
      { path: 'audiences', name: 'audiences', component: () => import('@/pages/Audiences.vue') },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/pages/Notifications.vue'),
      },
      {
        path: 'domain-monitoring',
        name: 'domain-monitoring',
        component: () => import('@/pages/DomainMonitoring.vue'),
      },
      { path: 'tasks', name: 'tasks', component: () => import('@/pages/Tasks.vue') },
      { path: 'settings', name: 'settings', component: () => import('@/pages/Settings.vue') },
    ],
  },

  // Backward-compatible console URLs. Older pages and user bookmarks used
  // top-level paths; keep them as redirects after public legal routes so
  // third-party review pages like /privacy and /terms remain unrestricted.
  { path: '/platforms', redirect: { name: 'platforms' } },
  { path: '/commerce', redirect: { name: 'commerce' } },
  { path: '/ad-accounts', redirect: { name: 'ad-accounts' } },
  { path: '/pages', redirect: { name: 'pages' } },
  { path: '/pixels', redirect: { name: 'pixels' } },
  { path: '/audiences', redirect: { name: 'audiences' } },
  { path: '/reports', redirect: { name: 'reports' } },
  { path: '/report-center', redirect: { name: 'report-center' } },
  { path: '/pivot-report', redirect: { name: 'pivot-report' } },
  { path: '/optimizer-efficiency', redirect: { name: 'optimizer-efficiency-report' } },
  { path: '/profit', redirect: { name: 'profit' } },
  { path: '/profit-drilldown', redirect: { name: 'profit-drilldown' } },
  { path: '/profit-breakdown', redirect: { name: 'profit-breakdown' } },
  { path: '/store-report', redirect: { name: 'store-report' } },
  { path: '/custom-report', redirect: { name: 'custom-report' } },
  { path: '/ad-management', redirect: { name: 'ad-management' } },
  { path: '/creative-lifecycle', redirect: { name: 'creative-lifecycle' } },
  { path: '/audience-lifecycle', redirect: { name: 'audience-lifecycle' } },
  { path: '/autopilot', redirect: { name: 'autopilot' } },
  { path: '/autopilot/policies', redirect: { name: 'autopilot-policies' } },
  { path: '/autopilot/actions', redirect: { name: 'autopilot-actions' } },
  { path: '/diagnostics', redirect: { name: 'diagnostics' } },
  { path: '/test-planner', redirect: { name: 'test-planner' } },
  { path: '/insights', redirect: { name: 'insights-library' } },
  { path: '/experiments', redirect: { name: 'experiments' } },
  { path: '/automation-rules', redirect: { name: 'automation-rules' } },
  { path: '/ai-assistant', redirect: { name: 'ai-assistant' } },
  { path: '/ad-library', redirect: { name: 'ad-library' } },
  { path: '/notifications', redirect: { name: 'notifications' } },
  { path: '/tasks', redirect: { name: 'tasks' } },
  { path: '/data-quality', redirect: { name: 'data-quality' } },
  { path: '/domain-monitoring', redirect: { name: 'domain-monitoring' } },
  { path: '/settings', redirect: { name: 'settings' } },
  { path: '/campaigns', redirect: { name: 'ad-management' } },
  { path: '/campaigns/:id', redirect: (to) => ({ name: 'campaign-detail', params: to.params }) },

  // ----------------------------------------------------------------------
  // 404
  // ----------------------------------------------------------------------
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFound.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

const featureKeyByRouteName = new Map<string, string>();
for (const section of navSections) {
  for (const item of [...(section.entry ? [section.entry] : []), ...section.items]) {
    if (item.featureKey) featureKeyByRouteName.set(item.name, item.featureKey);
  }
}
featureKeyByRouteName.set('creatives', 'reports.creative_board');
featureKeyByRouteName.set('campaigns', 'delivery.ad_management');
featureKeyByRouteName.set('campaign-detail', 'delivery.ad_management');
featureKeyByRouteName.set('optimizer-efficiency-report', 'reports.center');

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.initializeAuth();

  // Public pages are always accessible
  if (to.meta.public) return true;

  if (auth.sessionUnavailable) {
    if (to.name === 'internal-login') return true;
    return { name: 'internal-login', query: { session_unavailable: '1', redirect: to.fullPath } };
  }

  // Guest pages (login) — if already authenticated, redirect to console
  if (to.meta.guest) {
    return auth.isAuthenticated ? { name: 'dashboard' } : true;
  }

  // Everything else requires auth
  if (!auth.isAuthenticated) {
    return { name: 'internal-login', query: { redirect: to.fullPath } };
  }

  const featureKey = typeof to.name === 'string' ? featureKeyByRouteName.get(to.name) : undefined;
  if (featureKey && !auth.hasFeature(featureKey) && to.name !== 'dashboard') {
    return { name: 'dashboard' };
  }

  return true;
});
